import { jsx as _jsx, jsxs as _jsxs, Fragment as _Fragment } from "react/jsx-runtime";
import { useCallback, useEffect, useMemo, useRef, useState } from "react";
import { uiClass } from "./appearance.js";
import { FEATURED_WALLET_IDS, fetchWallets, filterWallets, } from "./explorer.js";
import { Icon } from "./Icon.js";
import { isMobile, openWalletLink, walletHref, walletLink } from "./link.js";
import { Modal } from "./Modal.js";
import { pairingRefreshDelay } from "./pairing.js";
import { QrCode } from "./QrCode.js";
const PAGE = 30;
const NO_PROJECT_ID = "No WalletConnect project ID for wallet listings. Create the pairing from a Konekt provider or connector, or pass projectId to the pairing hook.";
function idKey(ids) {
    return ids?.join(",") ?? "";
}
function idList(key) {
    return key ? key.split(",") : undefined;
}
function localFor(wallet, local) {
    return local.find((candidate) => {
        if (wallet.rdns && candidate.rdns && (candidate.rdns === wallet.rdns || candidate.rdns.endsWith(wallet.rdns))) {
            return true;
        }
        return candidate.name.toLowerCase() === wallet.name.toLowerCase();
    });
}
function WalletImage({ name, imageUrl, iconClass, unstyled, }) {
    if (imageUrl)
        return _jsx("img", { className: uiClass(iconClass ?? "", unstyled), src: imageUrl, alt: "" });
    const fallback = iconClass ? `${iconClass} kui-fallback` : "kui-fallback";
    return _jsx("span", { className: uiClass(fallback, unstyled), children: name[0]?.toUpperCase() ?? "?" });
}
function WalletRow({ name, imageUrl, tag, onClick, unstyled }) {
    return (_jsxs("button", { type: "button", className: uiClass("kui-row", unstyled), "data-kui-slot": "wallet", onClick: onClick, children: [_jsx(WalletImage, { name: name, imageUrl: imageUrl, iconClass: "kui-row-icon", unstyled: unstyled }), _jsx("span", { className: uiClass("kui-row-name", unstyled), title: name, children: name }), tag && _jsx("span", { className: uiClass("kui-tag", unstyled), children: tag })] }));
}
function WalletCard({ name, imageUrl, onClick, unstyled }) {
    return (_jsxs("button", { type: "button", className: uiClass("kui-card", unstyled), "data-kui-slot": "wallet", onClick: onClick, children: [_jsx(WalletImage, { name: name, imageUrl: imageUrl, unstyled: unstyled }), _jsx("span", { title: name, children: name })] }));
}
function WalletRowSkeleton({ unstyled }) {
    return (_jsxs("div", { className: uiClass("kui-row kui-row-skeleton", unstyled), "data-kui-slot": "wallet-skeleton", "aria-hidden": "true", children: [_jsx("span", { className: uiClass("kui-row-icon kui-skeleton-block", unstyled) }), _jsx("span", { className: uiClass("kui-skeleton-line", unstyled) })] }));
}
/**
 * Wallet picker and WalletConnect pairing dialog.
 *
 * The modal loads compatible wallets from WalletConnect Explorer and includes any local wallets
 * from the pairing binding. On a desktop browser it starts pairing when the user asks for a QR
 * code, and closing that view runs the teardown returned by `pairing.start`.
 *
 * On a phone it instead pairs as soon as it opens, lists only wallets reachable by a mobile link,
 * and leaves for the wallet inside the tap that chose it. Both are required: WebKit refuses to open
 * a custom scheme once the gesture that asked for it has expired, so the URI cannot be fetched
 * first. A pairing that is about to lapse is replaced with a fresh one.
 *
 * The dialog traps keyboard focus, closes on Escape, restores previous focus, and labels its
 * controls for assistive technology.
 */
export function WalletModal({ open, pairing, chains, wallets, onDismiss, onClose, className, style, theme, unstyled, }) {
    const { connected, local, connectLocal, start, reset, error: pairError, projectId } = pairing;
    const [view, setView] = useState("home");
    const [uri, setUri] = useState();
    const [selected, setSelected] = useState();
    const [featured, setFeatured] = useState([]);
    const [listed, setListed] = useState([]);
    const [total, setTotal] = useState(0);
    const [page, setPage] = useState(1);
    const [search, setSearch] = useState("");
    const [query, setQuery] = useState("");
    const [loading, setLoading] = useState(false);
    const [loaded, setLoaded] = useState(false);
    const [error, setError] = useState();
    const [copiedUri, setCopiedUri] = useState(false);
    const [featuredReady, setFeaturedReady] = useState(false);
    const [replacing, setReplacing] = useState(false);
    const opened = useRef(undefined);
    const mobile = isMobile();
    /** Only `pairing.start` is promised to be stable, so nothing else may restart a live pairing. */
    const latest = useRef({ reset, onDismiss });
    latest.current = { reset, onDismiss };
    const chainKey = idKey(chains ?? pairing.chains);
    const includeKey = idKey(wallets?.include);
    const excludeKey = idKey(wallets?.exclude);
    const featuredKey = idKey(wallets?.featured ?? wallets?.include ?? FEATURED_WALLET_IDS);
    const filter = useMemo(() => ({ include: idList(includeKey), exclude: idList(excludeKey) }), [includeKey, excludeKey]);
    const close = useCallback(() => {
        if (!connected)
            onDismiss?.();
        reset();
        setView("home");
        setUri(undefined);
        setSelected(undefined);
        setSearch("");
        setQuery("");
        setLoaded(false);
        setCopiedUri(false);
        setReplacing(false);
        onClose();
    }, [connected, onClose, onDismiss, reset]);
    useEffect(() => {
        if (connected && open)
            onClose();
    }, [connected, open, onClose]);
    useEffect(() => {
        if (!open)
            return;
        if (!projectId) {
            setError(NO_PROJECT_ID);
            setFeaturedReady(true);
            return;
        }
        let cancelled = false;
        const ids = idList(featuredKey) ?? [];
        setError(undefined);
        void fetchWallets({ projectId, ids, entries: ids.length, chains: idList(chainKey) })
            .then((r) => {
            if (!cancelled)
                setFeatured(filterWallets(r.wallets, filter));
        })
            .catch((e) => {
            if (!cancelled)
                setError(e instanceof Error ? e.message : String(e));
        })
            .finally(() => {
            if (!cancelled)
                setFeaturedReady(true);
        });
        return () => {
            cancelled = true;
        };
    }, [open, projectId, featuredKey, chainKey, filter]);
    useEffect(() => {
        const t = setTimeout(() => setQuery(search.trim()), 250);
        return () => clearTimeout(t);
    }, [search]);
    useEffect(() => {
        if (!open || view !== "all")
            return;
        if (!projectId) {
            setError(NO_PROJECT_ID);
            return;
        }
        let cancelled = false;
        const include = idList(includeKey);
        setLoading(true);
        setLoaded(false);
        setError(undefined);
        void fetchWallets({
            projectId,
            chains: idList(chainKey),
            ids: include,
            page: include ? undefined : 1,
            entries: include ? include.length : PAGE,
            search: query || undefined,
        })
            .then((r) => {
            if (cancelled)
                return;
            const found = filterWallets(r.wallets, filter);
            setListed(found);
            setTotal(include ? found.length : r.total);
            setPage(1);
        })
            .catch((e) => {
            if (!cancelled)
                setError(e instanceof Error ? e.message : String(e));
        })
            .finally(() => {
            if (!cancelled) {
                setLoading(false);
                setLoaded(true);
            }
        });
        return () => {
            cancelled = true;
        };
    }, [open, view, projectId, query, chainKey, includeKey, filter]);
    /** A phone pairs while the user is still choosing, so the deep link can open inside their tap. */
    const pairingWanted = open && (mobile || view === "connect");
    useEffect(() => {
        if (!pairingWanted)
            return;
        let stop = () => { };
        let timer;
        let live;
        let replaced = false;
        const onUri = (next) => {
            live = next;
            setUri(next);
            setReplacing(false);
            /** Whatever the discarded attempt reported on its way out is not this pairing's news. */
            if (replaced) {
                replaced = false;
                latest.current.reset();
            }
            const delay = pairingRefreshDelay(next);
            if (delay !== undefined)
                timer = setTimeout(replace, delay);
        };
        /** A pairing nobody can answer any more is worse than the wait for a new one. */
        function replace() {
            clearTimeout(timer);
            latest.current.onDismiss?.();
            stop();
            live = undefined;
            setUri(undefined);
            setReplacing(true);
            replaced = true;
            stop = start(onUri);
        }
        /** Timers are throttled or suspended in a hidden tab, so coming back is its own check. */
        const onVisible = () => {
            if (document.visibilityState === "visible" && live && pairingRefreshDelay(live) === 0)
                replace();
        };
        stop = start(onUri);
        document.addEventListener("visibilitychange", onVisible);
        return () => {
            clearTimeout(timer);
            document.removeEventListener("visibilitychange", onVisible);
            stop();
        };
    }, [pairingWanted, start]);
    /** Only the first attempt for a chosen wallet leaves on its own; a replacement waits to be asked. */
    useEffect(() => {
        if (!uri || !selected || !mobile || opened.current)
            return;
        const href = walletHref(selected, uri, true);
        if (!href)
            return;
        opened.current = href;
        openWalletLink(href);
    }, [uri, selected, mobile]);
    const featuredIds = idList(featuredKey) ?? [];
    const reachable = (list) => (mobile ? list.filter((w) => walletLink(w, true)) : list);
    const featuredOnly = reachable(featured.filter((w) => !localFor(w, local)));
    const showFeaturedSkeletons = !featuredReady && featured.length === 0 && featuredIds.length > 0;
    const pickWallet = (wallet) => {
        const match = localFor(wallet, local);
        if (match) {
            connectLocal(match);
            onClose();
            return;
        }
        opened.current = undefined;
        setSelected(wallet);
        setView("connect");
        if (!mobile || !uri)
            return;
        const href = walletHref(wallet, uri, true);
        if (!href)
            return;
        opened.current = href;
        openWalletLink(href);
    };
    const loadMore = () => {
        if (!projectId)
            return;
        const next = page + 1;
        setLoading(true);
        void fetchWallets({
            projectId,
            chains: idList(chainKey),
            page: next,
            entries: PAGE,
            search: query || undefined,
        })
            .then((r) => {
            setListed((prev) => [...prev, ...filterWallets(r.wallets, filter)]);
            setTotal(r.total);
            setPage(next);
        })
            .catch((e) => setError(e instanceof Error ? e.message : String(e)))
            .finally(() => setLoading(false));
    };
    const title = view === "all" ? "All wallets" : view === "connect" ? (selected?.name ?? "WalletConnect") : "Connect wallet";
    const href = uri && selected ? walletHref(selected, uri) : undefined;
    const connectError = view === "connect" && !replacing ? pairError : undefined;
    /** The wallet the phone leaves for, when it advertised a mobile link. A QR code serves the rest. */
    const leaveFor = mobile && selected && walletLink(selected, true) ? selected : undefined;
    const lead = leaveFor
        ? `Continue in ${leaveFor.name}, then come back here`
        : `Scan this QR code with ${selected?.name ?? "your wallet"}${mobile ? "" : " on your phone"}`;
    const goHome = () => {
        if (view === "connect") {
            setSelected(undefined);
            setCopiedUri(false);
            /** A phone keeps its pairing: it was started for the modal, not for one wallet. */
            if (!mobile) {
                onDismiss?.();
                reset();
                setUri(undefined);
            }
        }
        setView("home");
    };
    return (_jsxs(Modal, { open: open, title: title, onClose: close, className: className, style: style, theme: theme, unstyled: unstyled, children: [_jsxs("div", { className: uiClass("kui-head", unstyled), "data-kui-slot": "header", children: [view !== "home" && (_jsx("button", { type: "button", className: uiClass("kui-icon-btn", unstyled), "data-kui-slot": "back", "aria-label": "Back", onClick: goHome, children: _jsx(Icon, { name: "arrow-left" }) })), _jsx("h2", { children: title }), _jsx("button", { type: "button", className: uiClass("kui-icon-btn", unstyled), "data-kui-slot": "close", "aria-label": "Close", onClick: close, children: _jsx(Icon, { name: "close" }) })] }), _jsxs("div", { className: uiClass("kui-body", unstyled), "data-kui-slot": "body", children: [(error || connectError) && (_jsx("p", { className: uiClass("kui-error", unstyled), "data-kui-slot": "error", role: "alert", children: error ?? connectError })), view === "home" && (_jsxs("div", { className: uiClass("kui-list", unstyled), "data-kui-slot": "wallet-list", "aria-busy": featuredReady ? undefined : true, children: [_jsxs("button", { type: "button", className: uiClass("kui-row", unstyled), "data-kui-slot": "wallet-option", onClick: () => {
                                    setSelected(undefined);
                                    setView("connect");
                                }, children: [_jsx("span", { className: uiClass("kui-row-icon kui-row-icon-soft", unstyled), children: _jsx(Icon, { name: "qr" }) }), _jsx("span", { className: uiClass("kui-row-name", unstyled), children: "WalletConnect" }), _jsx("span", { className: uiClass("kui-tag", unstyled), children: "QR code" })] }), local.map((wallet) => (_jsx(WalletRow, { name: wallet.name, imageUrl: wallet.icon, tag: "Installed", unstyled: unstyled, onClick: () => {
                                    connectLocal(wallet);
                                    onClose();
                                } }, wallet.id))), showFeaturedSkeletons
                                ? featuredIds.map((id) => _jsx(WalletRowSkeleton, { unstyled: unstyled }, id))
                                : featuredOnly.map((w) => (_jsx(WalletRow, { name: w.name, imageUrl: w.imageUrl, unstyled: unstyled, onClick: () => pickWallet(w) }, w.id))), _jsxs("button", { type: "button", className: uiClass("kui-row", unstyled), "data-kui-slot": "wallet-option", onClick: () => setView("all"), children: [_jsx("span", { className: uiClass("kui-row-icon kui-row-icon-soft", unstyled), children: _jsx(Icon, { name: "grid" }) }), _jsx("span", { className: uiClass("kui-row-name", unstyled), children: "All wallets" }), _jsx(Icon, { className: uiClass("kui-chevron", unstyled), name: "chevron-right" })] })] })), view === "all" && (_jsxs(_Fragment, { children: [_jsxs("label", { className: uiClass("kui-search-wrap", unstyled), children: [_jsx(Icon, { name: "search" }), _jsx("input", { className: uiClass("kui-search", unstyled), "data-kui-slot": "search", value: search, onChange: (e) => setSearch(e.target.value), placeholder: "Search by wallet name", "aria-label": "Search wallets", autoComplete: "off" })] }), loading && listed.length === 0 && (_jsxs("div", { className: uiClass("kui-loading", unstyled), "data-kui-slot": "status", role: "status", "aria-live": "polite", children: [_jsx("span", { className: uiClass("kui-spinner", unstyled) }), "Loading wallets\u2026"] })), loaded && !loading && !error && listed.length === 0 && (_jsx("p", { className: uiClass("kui-status", unstyled), "data-kui-slot": "status", role: "status", children: "No wallets found." })), _jsx("div", { className: uiClass("kui-grid", unstyled), "data-kui-slot": "wallet-list", children: reachable(listed).map((w) => (_jsx(WalletCard, { name: w.name, imageUrl: w.imageUrl, unstyled: unstyled, onClick: () => pickWallet(w) }, w.id))) }), listed.length < total && (_jsx("button", { type: "button", className: uiClass("kui-ghost", unstyled), "data-kui-slot": "load-more", disabled: loading, onClick: loadMore, children: loading ? "Loading…" : "Load more wallets" }))] })), view === "connect" && (_jsxs("div", { className: uiClass("kui-qr-wrap", unstyled), "data-kui-slot": leaveFor ? "connecting" : "qr", children: [leaveFor ? (_jsxs("div", { className: uiClass("kui-leaving", unstyled), children: [_jsx(WalletImage, { name: leaveFor.name, imageUrl: leaveFor.imageUrl, iconClass: "kui-leaving-icon", unstyled: unstyled }), _jsx("span", { className: uiClass("kui-spinner", unstyled) })] })) : (_jsx("div", { className: uiClass("kui-qr-card", unstyled), children: uri ? (_jsx(QrCode, { value: uri, unstyled: unstyled })) : (_jsxs("div", { className: uiClass("kui-qr-waiting", unstyled), children: [_jsx("span", { className: uiClass("kui-spinner", unstyled) }), "Creating a secure connection\u2026"] })) })), _jsx("p", { className: uiClass("kui-qr-lead", unstyled), children: lead }), href && (_jsxs("button", { type: "button", className: uiClass("kui-primary", unstyled), "data-kui-slot": "open-wallet", onClick: () => openWalletLink(href), children: ["Open ", selected?.name ?? "wallet"] })), uri && (_jsxs("button", { type: "button", className: uiClass("kui-link", unstyled), "data-kui-slot": "copy-uri", onClick: () => {
                                    void navigator.clipboard.writeText(uri);
                                    setCopiedUri(true);
                                    window.setTimeout(() => setCopiedUri(false), 1500);
                                }, children: [_jsx(Icon, { name: copiedUri ? "check" : "copy" }), copiedUri ? "Link copied" : "Copy link"] })), _jsx("p", { className: uiClass("kui-qr-note", unstyled), children: "Never share your private keys or recovery phrase." })] }))] })] }));
}
//# sourceMappingURL=WalletModal.js.map